import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';
import { Extension } from '@tiptap/core';
import './RichTextEditor.css';

// Custom extension to handle Tab key for indentation
const IndentExtension = Extension.create({
  name: 'indent',

  addOptions() {
    return {
      types: ['paragraph'],
      minIndent: 0,
      maxIndent: 240,
      indentStep: 30,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: element => {
              const paddingLeft = element.style.paddingLeft;
              return paddingLeft ? parseInt(paddingLeft, 10) : 0;
            },
            renderHTML: attributes => {
              if (!attributes.indent || attributes.indent === 0) {
                return {};
              }
              return {
                style: `padding-left: ${attributes.indent}px`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent: () => ({ tr, state, dispatch }) => {
        const { selection } = state;
        const { $from } = selection;
        const node = $from.parent;

        if (!this.options.types.includes(node.type.name)) {
          return false;
        }

        const currentIndent = node.attrs.indent || 0;
        const newIndent = Math.min(
          currentIndent + this.options.indentStep,
          this.options.maxIndent
        );

        if (dispatch) {
          const pos = $from.before();
          tr.setNodeMarkup(pos, null, { ...node.attrs, indent: newIndent });
        }

        return true;
      },
      outdent: () => ({ tr, state, dispatch }) => {
        const { selection } = state;
        const { $from } = selection;
        const node = $from.parent;

        if (!this.options.types.includes(node.type.name)) {
          return false;
        }

        const currentIndent = node.attrs.indent || 0;
        const newIndent = Math.max(
          currentIndent - this.options.indentStep,
          this.options.minIndent
        );

        if (dispatch) {
          const pos = $from.before();
          tr.setNodeMarkup(pos, null, { ...node.attrs, indent: newIndent });
        }

        return true;
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        // For list items, use built-in sink command
        if (this.editor.isActive('listItem')) {
          return this.editor.commands.sinkListItem('listItem');
        }
        // For paragraphs, use custom indent command
        return this.editor.commands.indent();
      },
      'Shift-Tab': () => {
        // For list items, use built-in lift command
        if (this.editor.isActive('listItem')) {
          return this.editor.commands.liftListItem('listItem');
        }
        // For paragraphs, use custom outdent command
        return this.editor.commands.outdent();
      },
    };
  },
});

/**
 * RichTextEditor - Rich text editor for coin messages
 *
 * @param {Object} props
 * @param {string} props.value - HTML content
 * @param {Function} props.onChange - Callback (html) => void
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disabled state
 * @param {number} props.maxLength - Max characters (default 1000)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.error - Error message
 */
function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Write a message...',
  disabled = false,
  maxLength = 1000,
  className = '',
  error = ''
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable features we don't need
        heading: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      CharacterCount.configure({
        limit: maxLength,
      }),
      IndentExtension,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: 'editor-content-area',
        'data-placeholder': placeholder,
      },
    },
  });

  // Update editor content when value prop changes
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // Update disabled state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  const characterCount = editor.storage.characterCount.characters();
  const isOverLimit = characterCount > maxLength;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);

    if (url === null) {
      return; // Cancelled
    }

    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }

    // Add https:// if no protocol specified
    const finalUrl = url.match(/^https?:\/\//) ? url : `https://${url}`;
    editor.chain().focus().setLink({ href: finalUrl }).run();
  };

  const handleIndent = () => {
    // For list items, use sink command
    if (editor.isActive('listItem')) {
      editor.chain().focus().sinkListItem('listItem').run();
    } else {
      // For paragraphs, use custom indent command
      editor.chain().focus().indent().run();
    }
  };

  const handleOutdent = () => {
    // For list items, use lift command
    if (editor.isActive('listItem')) {
      editor.chain().focus().liftListItem('listItem').run();
    } else {
      // For paragraphs, use custom outdent command
      editor.chain().focus().outdent().run();
    }
  };

  return (
    <div className={`rich-text-editor ${disabled ? 'disabled' : ''} ${error ? 'error' : ''} ${className}`}>
      <div className="editor-toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`editor-toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`}
          disabled={disabled}
          title="Bold (Ctrl+B)"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`editor-toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`}
          disabled={disabled}
          title="Italic (Ctrl+I)"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`editor-toolbar-button ${editor.isActive('underline') ? 'is-active' : ''}`}
          disabled={disabled}
          title="Underline (Ctrl+U)"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`editor-toolbar-button ${editor.isActive('bulletList') ? 'is-active' : ''}`}
          disabled={disabled}
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`editor-toolbar-button ${editor.isActive('orderedList') ? 'is-active' : ''}`}
          disabled={disabled}
          title="Numbered List"
        >
          1. List
        </button>
        <button
          type="button"
          onClick={handleOutdent}
          className="editor-toolbar-button"
          disabled={disabled}
          title="Decrease Indent (Shift+Tab)"
        >
          ← Outdent
        </button>
        <button
          type="button"
          onClick={handleIndent}
          className="editor-toolbar-button"
          disabled={disabled}
          title="Increase Indent (Tab)"
        >
          Indent →
        </button>
        <button
          type="button"
          onClick={setLink}
          className={`editor-toolbar-button ${editor.isActive('link') ? 'is-active' : ''}`}
          disabled={disabled}
          title="Add Link"
        >
          🔗 Link
        </button>
      </div>

      <EditorContent editor={editor} className="editor-content" />

      <div className="editor-footer">
        <span className={`character-counter ${isOverLimit ? 'over-limit' : ''}`}>
          {characterCount}/{maxLength} characters
        </span>
      </div>

      {error && (
        <div className="editor-error">
          {error}
        </div>
      )}
    </div>
  );
}

export default RichTextEditor;
